import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { Tournament } from '@swiss-magic/shared-types';

// Shared close-registration dialog. Only shown when closing would
// leave pending registrations unresolved — the director must approve
// or reject them before the tournament can advance. Used by the
// dashboard card and the tournament hub.

interface Props {
  target: Tournament | null;
  pendingCount: number;
  onCancel: () => void;
  onApprove: () => void;
  onReject: () => void;
}

export function CloseRegistrationDialog({
  target,
  pendingCount,
  onCancel,
  onApprove,
  onReject,
}: Props) {
  const { t } = useTranslation('director');
  return (
    <Dialog open={target !== null} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent data-testid="close-registration-confirm-dialog">
        <DialogHeader>
          <DialogTitle>{t('dashboard.close_reg_dialog.title')}</DialogTitle>
          <DialogDescription>
            {pendingCount === 1
              ? t('dashboard.close_reg_dialog.body_one', { count: pendingCount })
              : t('dashboard.close_reg_dialog.body_other', { count: pendingCount })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button
            variant="outline"
            data-testid="cancel-close-registration"
            onClick={onCancel}
          >
            {t('dashboard.close_reg_dialog.go_back')}
          </Button>
          <Button data-testid="approve-and-close" onClick={onApprove}>
            {t('dashboard.close_reg_dialog.approve_and_close')}
          </Button>
          <Button
            variant="destructive"
            data-testid="reject-and-close"
            onClick={onReject}
          >
            {t('dashboard.close_reg_dialog.reject_and_close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
